import { useState } from "react";

import { format } from "date-fns";
import {
  Calendar,
  Calendar as CalendarIcon,
  Filter,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Save,
  Search,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/drawer";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Textarea } from "~/components/ui/textarea";
import { clients, purchaseOrders } from "~/data/mockData";
import { toast } from "~/hooks/use-toast";
import { PurchaseOrder } from "~/types/user";
import { formatCurrency, formatDate } from "~/utils/helpers";

type PaymentHistoryEntry = {
  type: "note" | "event";
  date: string;
  content: string;
  eventType?: "call" | "message" | "email" | "meeting";
};

type PaymentHistory = {
  [key: string]: PaymentHistoryEntry[];
};

const PaymentDashboard = () => {
  const [filterClient, setFilterClient] = useState("all");
  const [filterSeller, setFilterSeller] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PurchaseOrder | null>(
    null
  );
  const [noteText, setNoteText] = useState("");
  const [eventType, setEventType] = useState("call");
  const [eventDescription, setEventDescription] = useState("");

  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory>({});

  const pendingPayments = purchaseOrders.filter(
    (order) => order.status === "approved" && order.paymentStatus === "pending"
  );

  const latePayments = purchaseOrders.filter(
    (order) => order.status === "approved" && order.paymentStatus === "late"
  );

  const clearedPayments = purchaseOrders.filter(
    (order) => order.status === "approved" && order.paymentStatus === "cleared"
  );

  const filterOrders = (orders: PurchaseOrder[]) => {
    return orders.filter(
      (order) =>
        (filterClient === "all" || order.clientId === filterClient) &&
        (filterSeller === "all" || order.sellerId === filterSeller) &&
        (filterDateFrom === "" ||
          new Date(order.createdAt) >= new Date(filterDateFrom)) &&
        (filterDateTo === "" ||
          new Date(order.createdAt) <= new Date(filterDateTo)) &&
        (searchQuery === "" ||
          order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.clientName.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const filteredPendingPayments = filterOrders(pendingPayments);
  const filteredLatePayments = filterOrders(latePayments);
  const filteredClearedPayments = filterOrders(clearedPayments);

  const totalPending = filteredPendingPayments.reduce(
    (sum, order) => sum + order.total,
    0
  );
  const totalLate = filteredLatePayments.reduce(
    (sum, order) => sum + order.total,
    0
  );
  const totalCleared = filteredClearedPayments.reduce(
    (sum, order) => sum + order.total,
    0
  );

  const resetFilters = () => {
    setFilterClient("all");
    setFilterSeller("all");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSearchQuery("");
  };

  const paymentStatusTranslation = {
    pending: "Pendiente",
    late: "Atrasado",
    cleared: "Completado",
  };

  const handlePaymentClick = (payment: PurchaseOrder) => {
    setSelectedPayment(payment);
    setDrawerOpen(true);
  };

  const handleSaveNote = () => {
    if (!selectedPayment || !noteText.trim()) return;

    const paymentId = selectedPayment.id;
    const newEntry = {
      type: "note" as const,
      date: new Date().toISOString(),
      content: noteText,
    };

    setPaymentHistory((prev) => ({
      ...prev,
      [paymentId]: [...(prev[paymentId] || []), newEntry],
    }));

    setNoteText("");
    toast({
      title: "Nota guardada",
      description: "La nota ha sido guardada exitosamente.",
    });
  };

  const handleSaveEvent = () => {
    if (!selectedPayment || !eventDescription.trim()) return;

    const paymentId = selectedPayment.id;
    const newEntry = {
      type: "event" as const,
      date: new Date().toISOString(),
      content: eventDescription,
      eventType: eventType as "call" | "message" | "email" | "meeting",
    };

    setPaymentHistory((prev) => ({
      ...prev,
      [paymentId]: [...(prev[paymentId] || []), newEntry],
    }));

    setEventDescription("");
    toast({
      title: "Evento registrado",
      description: "El evento ha sido registrado exitosamente.",
    });
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case "call":
        return <Phone className="h-4 w-4" />;
      case "message":
        return <MessageSquare className="h-4 w-4" />;
      case "email":
        return <Mail className="h-4 w-4" />;
      case "meeting":
        return <CalendarIcon className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case "call":
        return "Llamada";
      case "message":
        return "Mensaje";
      case "email":
        return "Email";
      case "meeting":
        return "Reunión";
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="overflow-hidden rounded-2xl shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold">
              Pagos Pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-500">
              {formatCurrency(totalPending)}
            </div>
            <p className="text-sm text-muted-foreground">
              {filteredPendingPayments.length} notas
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden rounded-2xl shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold">Pagos Atrasados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-500">
              {formatCurrency(totalLate)}
            </div>
            <p className="text-sm text-muted-foreground">
              {filteredLatePayments.length} notas
            </p>
          </CardContent>
        </Card>

        <Card className="overflow-hidden rounded-2xl shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-bold">
              Pagos Completados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">
              {formatCurrency(totalCleared)}
            </div>
            <p className="text-sm text-muted-foreground">
              {filteredClearedPayments.length} notas
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden rounded-2xl shadow-md">
        <CardHeader className="flex flex-col items-start justify-between gap-4 pb-2 md:flex-row md:items-center">
          <CardTitle className="text-xl font-bold">Registro de Pagos</CardTitle>

          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por ID, cliente o factura..."
                className="w-[200px] rounded-full pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full"
            >
              <Filter className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={resetFilters}
            >
              Reiniciar
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="mb-4 grid grid-cols-1 gap-4 rounded-xl border p-4 md:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium">Cliente</label>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Todos los clientes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los clientes</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Vendedor</label>
              <Select value={filterSeller} onValueChange={setFilterSeller}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Todos los vendedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los vendedores</SelectItem>
                  <SelectItem value="2">Representante de Ventas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Fecha Desde
              </label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  className="rounded-xl pl-8"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Fecha Hasta
              </label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  className="rounded-xl pl-8"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Card className="overflow-hidden rounded-xl shadow-sm">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID de Nota</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ...filteredLatePayments,
                    ...filteredPendingPayments,
                    ...filteredClearedPayments,
                  ].map((order, index) => (
                    <TableRow
                      key={order.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                      onClick={() => handlePaymentClick(order)}
                    >
                      <TableCell className="font-medium">{order.id}</TableCell>
                      <TableCell>{order.clientName}</TableCell>
                      <TableCell>{formatDate(order.createdAt)}</TableCell>
                      <TableCell>{formatCurrency(order.total)}</TableCell>
                      <TableCell>
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${
                            order.paymentStatus === "cleared"
                              ? "bg-green-100 text-green-800"
                              : order.paymentStatus === "late"
                                ? "bg-red-100 text-red-800"
                                : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {order.paymentStatus &&
                            paymentStatusTranslation[
                              order.paymentStatus as keyof typeof paymentStatusTranslation
                            ]}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}

                  {[
                    ...filteredLatePayments,
                    ...filteredPendingPayments,
                    ...filteredClearedPayments,
                  ].length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-6 text-center text-muted-foreground"
                      >
                        No se encontraron registros de pago
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="max-h-[85vh]">
          <div className="mx-auto w-full max-w-4xl">
            <DrawerHeader>
              <DrawerTitle className="text-xl">
                Seguimiento de Pago: {selectedPayment?.id}
              </DrawerTitle>
              <DrawerDescription>
                Cliente: {selectedPayment?.clientName}
              </DrawerDescription>
            </DrawerHeader>

            <div className="p-4 pb-0">
              <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg border p-3">
                  <div className="mb-1 text-muted-foreground">
                    Fecha de Nota
                  </div>
                  <div className="font-medium">
                    {selectedPayment && formatDate(selectedPayment.createdAt)}
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="mb-1 text-muted-foreground">Monto Total</div>
                  <div className="font-medium">
                    {selectedPayment && formatCurrency(selectedPayment.total)}
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="mb-1 text-muted-foreground">
                    Estado de Pago
                  </div>
                  <div className="font-medium">
                    {selectedPayment?.paymentStatus && (
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          selectedPayment.paymentStatus === "cleared"
                            ? "bg-green-100 text-green-800"
                            : selectedPayment.paymentStatus === "late"
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {
                          paymentStatusTranslation[
                            selectedPayment.paymentStatus as keyof typeof paymentStatusTranslation
                          ]
                        }
                      </span>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="mb-1 text-muted-foreground">Vendedor</div>
                  <div className="font-medium">
                    {selectedPayment?.sellerName}
                  </div>
                </div>
              </div>

              <Tabs defaultValue="historial" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="historial">Historial</TabsTrigger>
                  <TabsTrigger value="notas">Agregar Nota</TabsTrigger>
                  <TabsTrigger value="eventos">Registrar Evento</TabsTrigger>
                </TabsList>

                <TabsContent value="historial" className="mt-4 space-y-4">
                  {selectedPayment &&
                  paymentHistory[selectedPayment.id]?.length > 0 ? (
                    <div className="space-y-3">
                      {paymentHistory[selectedPayment.id]
                        .sort(
                          (a, b) =>
                            new Date(b.date).getTime() -
                            new Date(a.date).getTime()
                        )
                        .map((entry, index) => (
                          <div key={index} className="rounded-lg border p-3">
                            <div className="mb-2 flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                {entry.type === "event" &&
                                  entry.eventType &&
                                  getEventIcon(entry.eventType)}
                                <span className="font-medium">
                                  {entry.type === "note"
                                    ? "Nota"
                                    : getEventTypeLabel(entry.eventType || "")}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {format(
                                  new Date(entry.date),
                                  "dd/MM/yyyy HH:mm"
                                )}
                              </span>
                            </div>
                            <p className="whitespace-pre-line text-sm">
                              {entry.content}
                            </p>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="py-6 text-center text-muted-foreground">
                      No hay registros para este pago
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notas" className="mt-4 space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Nota
                      </label>
                      <Textarea
                        placeholder="Ingrese sus notas sobre el pago aquí..."
                        className="min-h-[150px]"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                      />
                    </div>
                    <Button
                      className="w-full rounded-full"
                      onClick={handleSaveNote}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Guardar Nota
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="eventos" className="mt-4 space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Tipo de Evento
                      </label>
                      <Select value={eventType} onValueChange={setEventType}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Seleccionar tipo de evento" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              <span>Llamada</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="message">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              <span>Mensaje</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="email">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              <span>Email</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="meeting">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4" />
                              <span>Reunión</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Descripción
                      </label>
                      <Textarea
                        placeholder="Describa el evento o interacción..."
                        className="min-h-[100px]"
                        value={eventDescription}
                        onChange={(e) => setEventDescription(e.target.value)}
                      />
                    </div>

                    <Button
                      className="w-full rounded-full"
                      onClick={handleSaveEvent}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Registrar Evento
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <DrawerFooter>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setDrawerOpen(false)}
              >
                Cerrar
              </Button>
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default PaymentDashboard;
